import type { DataTypeIconKey, VariableDataTypeOption } from "@/constants/rule-variables";

import type { Option } from "../constants";
import type { VariableDataType } from "../hooks/useRuleVariables";
import type { OperatorOption } from "../types/configs";

/** Keep the first occurrence for each key (e.g. API returns one row per datatype). */
export function dedupeOptionsByKey<T extends Option>(
	items: T[],
	keyGetter: (item: T) => string,
): T[] {
	const seen = new Set<string>();
	const out: T[] = [];
	for (const item of items) {
		const key = keyGetter(item).trim();
		if (!key || seen.has(key)) continue;
		seen.add(key);
		out.push(item);
	}
	return out;
}

export function mapFormulaOperatorToOption(op: OperatorOption & { label?: string }): Option {
	return {
		label: op.label ?? op.operator,
		value: op.operator,
	};
}

export function mapGlobalConstantToOption(raw: unknown): Option | null {
	if (!raw || typeof raw !== "object") return null;
	const r = raw as Record<string, unknown>;
	if (r.id == null || r.id === "") return null;
	const id = String(r.id);
	const name = typeof r.name === "string" ? r.name.trim() : "";
	const label =
		typeof r.label === "string" && r.label.trim() ? r.label.trim() : name;
	return { label: label || id, value: id };
}

export function mapExpressionFunctionToOption(raw: unknown): Option | null {
	if (!raw || typeof raw !== "object") return null;
	const r = raw as Record<string, unknown>;
	const nameRaw = r.name;
	const name = typeof nameRaw === "string" ? nameRaw.trim() : "";
	if (!name) return null;
	const sigRaw = r.signature;
	const signature =
		typeof sigRaw === "string" && sigRaw.trim()
			? sigRaw.trim()
			: name;
	const descRaw = r.description;
	const description =
		typeof descRaw === "string" && descRaw.trim() ? descRaw.trim() : undefined;
	return {
		label: signature,
		value: name,
		insertText: signature,
		...(description !== undefined ? { description } : {}),
	};
}

/** Accepts a GET getDataTypes response body or a bare array. */
export function normalizeDataTypesApiPayload(raw: unknown): unknown[] {
	if (Array.isArray(raw)) return raw;
	if (raw && typeof raw === "object") {
		const o = raw as Record<string, unknown>;
		const nested = o.dataTypes ?? o.items ?? o.data;
		if (Array.isArray(nested)) return nested;
	}
	return [];
}

export function mapExpressionDataTypeToOption(raw: unknown): VariableDataTypeOption | null {
	if (!raw || typeof raw !== "object") return null;
	const r = raw as Record<string, unknown>;
	const val = typeof r?.name === "string" ? r?.name?.replace(" ", "").toLowerCase() : "";
	return {id: Number(r?.id), label: typeof r?.name === "string" ? r?.name : "", value: val as VariableDataType, iconKey: val as DataTypeIconKey};
}
